/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/*
 * This function is written to handle multiple promises received from rendition provider
 * (ajs and dcapi) client can add callbacks for both promises in do function of this class.
 * functionality like choosing which promise to resolve when can be added here.
 */

import * as constants from "constants/constants";
import * as stores from "stores";
import { isCurrentDeviceMobile } from "dc-preview-utils/src/Utils/DeviceUtils";
import {
  getFileName,
  getTitle,
  getDocInfo,
  getFileSize,
  getTotalPages,
  getIsTaggedPDF,
  getPDFVersion,
  getIsLinearizedWorkflow
} from "selectors/documentSelectors";
import { getCurrentPageIndex } from "selectors/pageNumber";
import { getPageOrigWidth, getPageOrigHeight } from "selectors/pageSelectors";
import { locale2 as locale, envUtil, providers } from "dc-core";
import { getFileLocation } from "selectors/appSelectors";

export const pathExistsInPathName = path => {
  const pathName = window.location.pathname;
  if (pathName.indexOf(path) !== -1) {
    return true;
  }
  return false;
};

export const isSharedFile = () => {
  const { reviewPathName, trackPathName } = constants;
  if (pathExistsInPathName(reviewPathName) || pathExistsInPathName(trackPathName)) {
    return true;
  }
  return false;
};

export const isSharedFileOnMobile = () => isCurrentDeviceMobile() && isSharedFile();

export const isDurableLink = () => {
  const pathname = window.location.pathname.replace(/\/$/, "");
  return /^\/id\//.test(pathname) || /^\/bench\/id\//.test(pathname);
};

function convertPdfDate(input) {
  if (input) {
    // pdf date format is D:YYYYMMDDhhmmssOHH'MM' as mentioned in pdf_reference mentioned in section 3.8.3
    // this funtion takes date in this pdf format and returns a date in DD/MM/YY, hh:mm:ss AM/PM format
    const year = parseInt(input.substring(2, 6));
    const month = parseInt(input.substring(6, 8)) - 1 || 0; // Subtract 1 because months are zero-based in JavaScript
    const day = parseInt(input.substring(8, 10)) || 1;
    const hour = parseInt(input.substring(10, 12)) || 0;
    const minute = parseInt(input.substring(12, 14)) || 0;
    const second = parseInt(input.substring(14, 16)) || 0;
    const offsetSign = input.charAt(16);
    const offsetHours = parseInt(input.substring(17, 19)) || 0;
    const offsetMinutes = parseInt(input.substring(20, 22)) || 0;
    const date = new Date(Date.UTC(year, month, day, hour, minute, second));
    const offsetInMinutes = offsetHours * 60 + offsetMinutes;
    const offsetInMilliseconds = offsetInMinutes * 60 * 1000;

    if (offsetSign === "-") {
      date.setTime(date.getTime() + offsetInMilliseconds);
    } else if (offsetSign === "+") {
      date.setTime(date.getTime() - offsetInMilliseconds);
    }

    const options = {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true
    };
    const loc = locale.getLocale() || "en-US";
    const output = date.toLocaleString(loc, options).replace(/(\d{2})\/(\d{2})\/(\d{2})/, "$2/$1/$3");
    return output;
  }
  return input;
}

export const getBasicDocProperties = () => {
  const fileName = getFileName(stores);
  const title = getTitle(stores);
  const docInfo = getDocInfo(stores);
  return {
    fileName: fileName || title,
    title: docInfo.Title,
    author: docInfo.Author,
    createdOn: convertPdfDate(docInfo.CreationDate),
    modifiedOn: convertPdfDate(docInfo.ModDate),
    creatorApp: docInfo.Creator,
    subject: docInfo.Subject,
    keywords: docInfo.Keywords
  };
};

export const getAdvanceDocProperties = () => {
  const fileSizeBytes = getFileSize(stores);
  const fileSizeKBytes = (fileSizeBytes / 1024).toFixed(2);
  const fileSizeMBytes = (fileSizeKBytes / 1024).toFixed(2);
  let fileSize = `(${fileSizeBytes.toLocaleString()} Bytes)`;
  if (fileSizeMBytes >= 1) {
    fileSize = `${fileSizeMBytes.toLocaleString()} MB ${fileSize}`;
  } else if (fileSizeKBytes >= 1) {
    fileSize = `${fileSizeKBytes.toLocaleString()} KB ${fileSize}`;
  }
  const docInfo = getDocInfo(stores);
  const numPages = getTotalPages(stores);
  const isTaggedPDF = getIsTaggedPDF(stores);
  const pageIndex = getCurrentPageIndex(stores);
  const height = getPageOrigHeight(stores, pageIndex);
  const width = getPageOrigWidth(stores, pageIndex);
  const ppi = constants.PIXELS_PER_INCH;
  const fileLocation = getFileLocation(stores);
  const pdfVersion = getPDFVersion(stores);

  return {
    pdfProducer: docInfo.Producer,
    pdfVersion,
    location: fileLocation,
    fileSize: fileSizeBytes && fileSize,
    pageSize: `${(width / ppi).toFixed(2)} x ${(height / ppi).toFixed(2)} in`,
    numPages,
    isTaggedPDF
  };
};

export const getViewerAnalyticsForKey = async eventType => {
  try {
    const integrationFactory = await providers.integrationProvider();
    const analyticsObject = await integrationFactory.getViewerAnalytics(eventType);
    return analyticsObject;
  } catch (error) {
    return {};
  }
};
